import React from 'react';
import PropTypes from 'prop-types';
import { Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import { MultiSelect } from 'react-multi-select-component';
import { useTranslation } from 'react-i18next';
import { CountriesList } from '../../../Components/helper';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';

function DistributionCentreRow({
  index,
  register,
  errors,
  watch,
  onCountryNameChange,
  canRemove,
  onRemove,
  validateAccount,
  currencyOptions,
  onCurrencyChange,
  brandOptions,
  subBrandOptions,
  onBrandChange,
  onSubBrandChange,
  subBrandAccess,
}) {
  const { t } = useTranslation('common');
  const { t: taccount } = useTranslation('account');

  const FIELD_PREFIX = `distributionCentres.${index}`;

  const countriesList = React.useMemo(
    () =>
      CountriesList?.map((item, idx) => ({
        label: item,
        value: item,
        key: `country-${idx}-${item}`,
      })) || [],
    [],
  );

  const getFieldName = (fieldName) => `${FIELD_PREFIX}.${fieldName}`;

  const fieldNames = {
    distributionName: getFieldName('distributionName'),
    buildingNumber: getFieldName('buildingNumber'),
    streetAddress: getFieldName('streetAddress'),
    city: getFieldName('city'),
    countryName: getFieldName('countryName'),
    postalCode: getFieldName('postalCode'),
    currency: getFieldName('currency'),
    brands: getFieldName('brands'),
    subBrands: getFieldName('subBrands'),
  };

  const getFieldError = (fieldName) => {
    return errors?.distributionCentres?.[index]?.[fieldName]?.message;
  };

  const currentValues = {
    distributionName: watch(fieldNames.distributionName),
    buildingNumber: watch(fieldNames.buildingNumber),
    streetAddress: watch(fieldNames.streetAddress),
    city: watch(fieldNames.city),
    countryName: watch(fieldNames.countryName),
    postalCode: watch(fieldNames.postalCode),
    currency: watch(fieldNames.currency),
    brands: watch(fieldNames.brands) || [],
    subBrands: watch(fieldNames.subBrands) || [],
  };

  const selectedBrandIds = React.useMemo(
    () => (Array.isArray(currentValues.brands) ? currentValues.brands.map((b) => b?.value).filter(Boolean) : []),
    [currentValues.brands],
  );

  const filteredSubBrandOptions = React.useMemo(() => {
    if (!selectedBrandIds.length) return [];
    const set = new Set(selectedBrandIds.map(String));
    return (subBrandOptions || []).filter((sb) => set.has(String(sb?.brandId)));
  }, [subBrandOptions, selectedBrandIds]);

  const handleCountryChange = (country) => {
    onCountryNameChange(country, index);
  };

  const handleRemove = () => {
    if (onRemove && canRemove) {
      onRemove(index);
    }
  };

  const handleBrandChange = (val) => {
    onBrandChange(val || [], index);
  };

  const handleSubBrandChange = (val) => {
    onSubBrandChange(val || [], index);
  };

  const removeButton = canRemove && (
    <CustomTooltip tooltipText={t('ACCOUNT_DETAILS.REMOVE_DISTRIBUTION_CENTRE')}>
      <button
        type="button"
        className="btn btn-action cursor-pointer text-dark text-decoration-none ml-auto p-0 pl-2 d-inline-flex mt-delete-btn"
        onClick={handleRemove}
        aria-label={t('ACCOUNT_DETAILS.REMOVE_DISTRIBUTION_CENTRE')}
      >
        <i className="icon-Delete f-24" />
      </button>
    </CustomTooltip>
  );

  return (
    <>
      <Col md={2}>
        <Form.Group className="common-input-control pt-0">
          <p className="field-name">
            {t('ACCOUNT_DETAILS.DISTRIBUTION_NAME')} <span className="required">*</span>
          </p>
          <Form.Control
            {...register(fieldNames.distributionName, {
              required: 'Distribution Details required',
              validate: validateAccount,
            })}
            className={currentValues.distributionName && 'data-filled form-control'}
            placeholder="Enter Distribution Name"
          />
          {getFieldError('distributionName') && <span className="error-message position-absolute d-block">{getFieldError('distributionName')}</span>}
        </Form.Group>
      </Col>

      <Col md={1}>
        <Form.Group className="common-input-control pt-0">
          <p className="field-name">
            {t('USER_SETTING.ACCOUNT_DETAIL.BUILDING_NO')} <span className="required">*</span>
          </p>
          <Form.Control
            {...register(fieldNames.buildingNumber, {
              required: t('USER_SETTING.ACCOUNT_DETAIL.BUILDING_NUMBER_REQUIRED'),
              validate: validateAccount,
            })}
            className={currentValues.buildingNumber && 'data-filled form-control'}
            placeholder="Enter Building No"
          />
          {getFieldError('buildingNumber') && <span className="error-message position-absolute d-block">{getFieldError('buildingNumber')}</span>}
        </Form.Group>
      </Col>

      <Col md={1} className="distribution-centre-street">
        <Form.Group className="common-input-control pt-0">
          <p className="field-name">
            {t('USER_SETTING.ACCOUNT_DETAIL.STREET_DETAILS')} <span className="required">*</span>
          </p>
          <Form.Control
            {...register(fieldNames.streetAddress, {
              required: t('USER_SETTING.ACCOUNT_DETAIL.STREET_ADDRESS'),
              validate: validateAccount,
            })}
            className={currentValues.streetAddress && 'data-filled form-control'}
            placeholder="Enter Street Address"
          />
          {getFieldError('streetAddress') && <span className="error-message position-absolute d-block">{getFieldError('streetAddress')}</span>}
        </Form.Group>
      </Col>

      <Col md={1}>
        <Form.Group className="common-input-control pt-0">
          <p className="field-name">
            {t('USER_SETTING.ACCOUNT_DETAIL.TOWN_CITY')} <span className="required">*</span>
          </p>
          <Form.Control
            {...register(fieldNames.city, {
              required: t('USER_SETTING.ACCOUNT_DETAIL.CITY'),
              validate: validateAccount,
            })}
            className={currentValues.city && 'data-filled form-control'}
            placeholder="Enter Town/City"
          />
          {getFieldError('city') && <span className="error-message position-absolute d-block">{getFieldError('city')}</span>}
        </Form.Group>
      </Col>

      <Col md={subBrandAccess ? 1 : 2}>
        <Form.Group className=" pt-0">
          <div className="common-react-select">
            <p className="field-name">
              {t('ACCOUNT_DETAILS.COUNTRY')} <span className="required">*</span>
            </p>
            <Select
              options={countriesList}
              getOptionLabel={({ label }) => `${label}`}
              getOptionValue={({ value }) => `${value}`}
              {...register(fieldNames.countryName, {
                required: t('USER_SETTING.ACCOUNT_DETAIL.COUNTRY_REQUIRED'),
                validate: validateAccount,
              })}
              className="common-select"
              classNamePrefix="common-select"
              isClearable
              menuPosition="fixed"
              menuShouldBlockScroll
              menuShouldScrollIntoView
              maxMenuHeight={144}
              onChange={handleCountryChange}
              placeholder={t('USER_SETTING.ACCOUNT_DETAIL.SELECT_COUNTRY')}
              value={countriesList.find((option) => option?.value === currentValues.countryName)}
            />
            {getFieldError('countryName') && <span className="error-message position-absolute d-block">{getFieldError('countryName')}</span>}
          </div>
        </Form.Group>
      </Col>

      <Col md={1}>
        <Form.Group className="common-input-control pt-0">
          <p className="field-name">
            {t('USER_SETTING.ACCOUNT_DETAIL.POSTAL_CODE')} <span className="required">*</span>
          </p>
          <Form.Control
            {...register(fieldNames.postalCode, {
              required: t('USER_SETTING.ACCOUNT_DETAIL.ZIP_CODE'),
              validate: validateAccount,
            })}
            className={currentValues.postalCode && 'data-filled form-control'}
            placeholder="Enter Postal Code"
          />
          {getFieldError('postalCode') && <span className="error-message position-absolute d-block">{getFieldError('postalCode')}</span>}
        </Form.Group>
      </Col>

      <Col md={1}>
        <Form.Group className="pt-0">
          <div className="common-react-select">
            <p className="field-name">
              {taccount('USER_SETTING.ACCOUNT_DETAIL.CURRENCY')} <span className="required">*</span>
            </p>
            <Select
              options={currencyOptions}
              getOptionLabel={({ label }) => `${label}`}
              getOptionValue={({ label }) => `${label}`}
              {...register(fieldNames.currency, {
                required: t('USER_SETTING.ACCOUNT_DETAIL.CURRENCY_REQUIRED'),
                validate: validateAccount,
              })}
              className="common-select"
              classNamePrefix="common-select"
              menuPosition="fixed"
              menuShouldBlockScroll
              menuShouldScrollIntoView
              maxMenuHeight={144}
              onChange={(val) => onCurrencyChange(val, index)}
              placeholder={t('USER_SETTING.ACCOUNT_DETAIL.SELECT_CURRENCY')}
              value={currencyOptions?.find((o) => o.label === currentValues.currency) || null}
              isClearable
            />
            {getFieldError('currency') && <span className="error-message position-absolute d-block">{getFieldError('currency')}</span>}
          </div>
        </Form.Group>
      </Col>

      <Col md={subBrandAccess ? 2 : 3} className="distribution-centre-brand">
        <div className="d-flex align-items-start">
          <Form.Group className="pt-0 flex-fill">
            <div className="common-react-select">
              <p className="field-name">{taccount('ACCOUNT_DETAILS.BRAND')}</p>
              <MultiSelect
                options={brandOptions}
                value={currentValues.brands}
                className="product-custom-multiselect common-new-multiselect w-100"
                labelledBy={taccount('ACCOUNT_DETAILS.BRAND')}
                overrideStrings={{
                  selectSomeItems: taccount('ACCOUNT_DETAILS.SELECT_BRAND'),
                  search: taccount('ACCOUNT_DETAILS.SEARCH_BRAND'),
                }}
                ClearSelectedIcon={null}
                onChange={handleBrandChange}
              />
            </div>
          </Form.Group>
          {!subBrandAccess && removeButton}
        </div>
      </Col>

      {subBrandAccess && (
        <Col md={2} className="distribution-centre-sub-brand">
          <div className="d-flex align-items-start">
            <Form.Group className="pt-0 flex-fill">
              <div className="common-react-select">
                <p className="field-name">{taccount('ACCOUNT_DETAILS.SUB_BRAND')}</p>
                <MultiSelect
                  options={filteredSubBrandOptions}
                  value={currentValues.subBrands}
                  className="product-custom-multiselect common-new-multiselect w-100"
                  labelledBy={taccount('ACCOUNT_DETAILS.SUB_BRAND')}
                  overrideStrings={{
                    selectSomeItems: taccount('ACCOUNT_DETAILS.SELECT_SUB_BRAND'),
                    search: taccount('ACCOUNT_DETAILS.SEARCH_SUB_BRAND'),
                  }}
                  ClearSelectedIcon={null}
                  onChange={handleSubBrandChange}
                  disabled={!selectedBrandIds.length}
                />
              </div>
            </Form.Group>
            {removeButton}
          </div>
        </Col>
      )}
    </>
  );
}

DistributionCentreRow.propTypes = {
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    distributionCentres: PropTypes.arrayOf(
      PropTypes.shape({
        distributionName: PropTypes.shape({ message: PropTypes.string }),
        buildingNumber: PropTypes.shape({ message: PropTypes.string }),
        streetAddress: PropTypes.shape({ message: PropTypes.string }),
        city: PropTypes.shape({ message: PropTypes.string }),
        countryName: PropTypes.shape({ message: PropTypes.string }),
        postalCode: PropTypes.shape({ message: PropTypes.string }),
        currency: PropTypes.shape({ message: PropTypes.string }),
      }),
    ),
  }),
  watch: PropTypes.func.isRequired,
  onCountryNameChange: PropTypes.func.isRequired,
  canRemove: PropTypes.bool.isRequired,
  onRemove: PropTypes.func,
  validateAccount: PropTypes.func.isRequired,
  currencyOptions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
  onCurrencyChange: PropTypes.func.isRequired,
  brandOptions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
  subBrandOptions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string, brandId: PropTypes.string })),
  onBrandChange: PropTypes.func.isRequired,
  onSubBrandChange: PropTypes.func.isRequired,
  subBrandAccess: PropTypes.bool,
};

DistributionCentreRow.defaultProps = {
  errors: {},
  onRemove: null,
  currencyOptions: [],
  brandOptions: [],
  subBrandOptions: [],
  subBrandAccess: false,
};

export default DistributionCentreRow;
